"use client";

import RegisterContent from "./RegisterContent";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { SendOtpParamSchema } from "@/types/auth";
import { useAuthModal } from "@/hooks/auth/useAuthModal";
import { useFormatPhone } from "@/hooks/useFormatPhone";
import { useSendOtp } from "@/hooks/auth/useAuth";

type RegisterFormFields = {
  phone: string;
};

type RegisterStepProps = {
  onLoginClick: () => void;
  onOtpSent: (phone: string) => void;
};

const getRegisterPayload = (rawPhone: string) => {
  const normalizedPhone = rawPhone.replace(/\D/g, "");
  return SendOtpParamSchema.parse({
    countryCode: "855",
    phone: normalizedPhone,
    type: "register",
    method: "phone",
  });
};

const RegisterStep = ({ onLoginClick, onOtpSent }: RegisterStepProps) => {
  const { phone, setVerificationToken } = useAuthModal();
  const { formatPhone } = useFormatPhone();

  const methods = useForm<RegisterFormFields>({
    defaultValues: {
      phone: formatPhone(phone, "KH"),
    },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;

  const currentPhone = watch("phone");

  const { mutate: sendOtp, isPending } = useSendOtp({
    onSuccess: (response) => {
      const responseData = response.data as
        | { verificationToken?: string }
        | undefined;

      if (
        responseData &&
        typeof responseData.verificationToken === "string"
      ) {
        void setVerificationToken(responseData.verificationToken);
      }

      sessionStorage.setItem(
        "otp_resend_expiry",
        (Date.now() + 120 * 1000).toString(),
      );
      sessionStorage.setItem("auth_flow", "register");

      const normalizedPhone = currentPhone.replace(/\D/g, "");
      onOtpSent(normalizedPhone);
    },
    onError: (message) => {
      setError("phone", {
        type: "server",
        message,
      });
    },
  });

  const handlePhoneChange = useCallback(
    (nextValue: string) => {
      clearErrors();
      setValue("phone", nextValue, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [clearErrors, setValue],
  );

  const onSubmit = (data: RegisterFormFields) => {
    if (isPending) return;
    clearErrors();

    try {
      const payload = getRegisterPayload(data.phone);
      sendOtp(payload);
    } catch {
      setError("phone", {
        type: "validation",
        message: "Enter a valid phone number.",
      });
    }
  };

  return (
    <RegisterContent
      phone={currentPhone}
      onPhoneChange={handlePhoneChange}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isPending}
      errorMessage={errors.phone?.message || errors.root?.message}
      onLoginClick={onLoginClick}
    />
  );
};

export default RegisterStep;
